/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, CircularProgress } from '@mui/material';
import { Async, F1 } from '../../helpers/types';
import { createContext, useContext } from 'react';

type AsyncRendererProps<T, E> = {
  data: Async<T, E>;
  loadRender?: JSX.Element;
  renderer?: (value: T) => JSX.Element;
};

const defaultLoader = (
  <Box
    className="flex justify-center items-center"
    sx={{ height: '20vh', minWidth: '200px' }}
  >
    <CircularProgress data-testid="defaultLoader" color="info" />
  </Box>
);

export const AsyncRenderer = <TItem, E>({
  renderer,
  loadRender,
  data,
}: AsyncRendererProps<TItem, E>) => {
  switch (data.type) {
    case 'fetch-error':
      return (
        <p>
          Error{data.value ? ': ' : ''}
          <b>
            {typeof data.value === 'string'
              ? data.value
              : JSON.stringify(data.value, null, 2)}
          </b>
        </p>
      );
    case 'not-fetched':
      return <></>;
    case 'fetching':
      return <>{loadRender || defaultLoader}</>;

    case 'fetched':
      return <>{renderer ? renderer(data.value) : null}</>;
  }
};

const _contexts: { [K in symbol]: React.Context<any> } = {} as any;
export const registerContext = <V, Params>(
  namePrefix: string,
  useValueProvider: F1<Params, Async<V>>,
) => {
  const name = Symbol(namePrefix);
  _contexts[name] = createContext<V>({} as any);

  const ValueProvider = (p: {
    params: Params;
    renderer: (value: V) => JSX.Element;
  }) => {
    const vm = useValueProvider(p.params);
    const c = _contexts[name];
    return (
      <AsyncRenderer
        data={vm}
        renderer={(value) => (
          <c.Provider value={value}>{p.renderer(value)}</c.Provider>
        )}
      />
    );
  };

  const useValue = (): V => {
    const c = _contexts[name];
    if (!c) throw new Error(`${name.toString()} context was not created yet`);
    return useContext(c);
  };
  return { ValueProvider, useValue };
};
